import { useState, useEffect } from 'react';

function TagModal({ onClose, onSelectTag, selectedTag, selectedTags, onConfirm, availableTags = [], boardName = '' }) {
  const [tags, setTags] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  // Support both single and multiple selection
  const [localSelectedTags, setLocalSelectedTags] = useState(
    selectedTags ? new Set(selectedTags) : (selectedTag ? new Set([selectedTag]) : new Set())
  );

  useEffect(() => {
    // If board has specific tags, use them; otherwise load default tags
    if (availableTags && availableTags.length > 0) {
      // Convert array to object format for consistency
      setTags({ [boardName || 'Tags']: availableTags });
    } else {
      // Load default tags from JSON file
      fetch('/default-tags.json')
        .then(res => res.json())
        .then(data => setTags(data.categories || {}))
        .catch(err => console.error('Error loading tags:', err));
    }
  }, [availableTags, boardName]);

  const handleTagClick = (tag) => {
    const newTags = new Set(localSelectedTags);
    if (newTags.has(tag)) {
      newTags.delete(tag);
    } else {
      newTags.add(tag);
    }
    setLocalSelectedTags(newTags);
  };

  const handleConfirm = () => {
    // Support both callback types
    if (onConfirm) {
      onConfirm(localSelectedTags);
    } else if (onSelectTag) {
      // For single selection compatibility
      const tagsArray = Array.from(localSelectedTags);
      onSelectTag(tagsArray[0] || '');
    }
    onClose();
  };

  const filteredTags = {};
  Object.entries(tags).forEach(([category, categoryTags]) => {
    const filtered = categoryTags.filter(tag =>
      tag.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredTags[category] = filtered;
    }
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Chọn thẻ</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full transition-all"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          {boardName && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="material-symbols-rounded text-blue-600 dark:text-blue-400 text-[18px]">folder</span>
              <span>Tags của bảng <span className="font-semibold text-gray-800 dark:text-gray-200">"{boardName}"</span></span>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="relative flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
            <span className="material-symbols-rounded text-gray-400 mr-2">search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm kiếm thẻ..."
              className="w-full border-none focus:ring-0 text-sm bg-transparent text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        {/* Tags List */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-800">
          {Object.keys(filteredTags).length === 0 ? (
            <div className="flex items-center justify-center h-20">
              <span className="text-sm text-gray-500">Không tìm thấy thẻ nào</span>
            </div>
          ) : (
            Object.entries(filteredTags).map(([category, categoryTags]) => (
              <div key={category} className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                  {category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {categoryTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => handleTagClick(tag)}
                      className={`px-3 py-1.5 border rounded-full text-sm transition-colors ${
                        localSelectedTags.has(tag)
                          ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-gray-900 dark:hover:border-gray-100'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-xl text-sm font-semibold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md transition-all transform active:scale-95"
          >
            Xác nhận
          </button>
        </div>
      </div>
    </div>
  );
}

export default TagModal;
